/**
 * System Initializer
 * 
 * Manages the initialization sequence for all system components
 * ensuring proper dependency order and error handling
 */

import { logger } from '../utils/logger';
import { performanceInitializer } from './api/performance-init';
import { cacheManager } from './api/cache-manager';
import { apiMonitoringService } from './api/monitoring';

interface InitializationStep {
  name: string;
  initialize: () => Promise<void>;
  required: boolean;
  dependencies: string[];
}

interface SystemInitConfig {
  enablePerformanceOptimization: boolean;
  enableCaching: boolean;
  enableMonitoring: boolean;
  development: boolean;
}

class SystemInitializer {
  private initialized = false;
  private initializationSteps: InitializationStep[] = [];
  private completedSteps = new Set<string>();
  private failedSteps = new Set<string>();

  constructor() {
    this.setupInitializationSteps();
  }

  /**
   * Initialize all system components with proper error handling
   */
  async initialize(config: Partial<SystemInitConfig> = {}): Promise<void> {
    if (this.initialized) {
      logger.warn('System already initialized', { module: 'SystemInitializer' });
      return;
    }

    const fullConfig: SystemInitConfig = {
      enablePerformanceOptimization: true,
      enableCaching: true,
      enableMonitoring: true,
      development: import.meta.env.DEV,
      ...config
    };

    logger.info('Starting system initialization', { 
      module: 'SystemInitializer',
      metadata: { config: fullConfig }
    });

    try {
      await this.executeInitializationSequence(fullConfig);
      this.initialized = true;
      
      logger.info('System initialization completed successfully', {
        module: 'SystemInitializer',
        metadata: {
          completedSteps: Array.from(this.completedSteps),
          failedSteps: Array.from(this.failedSteps)
        }
      });

      // Setup development tools if in development mode
      if (fullConfig.development) {
        this.setupDevelopmentTools();
      }

    } catch (error) {
      logger.error('System initialization failed', error as Error, {
        module: 'SystemInitializer',
        metadata: {
          completedSteps: Array.from(this.completedSteps),
          failedSteps: Array.from(this.failedSteps)
        }
      });
      throw error;
    }
  }

  /**
   * Get initialization status
   */
  getStatus() {
    return {
      initialized: this.initialized,
      completedSteps: Array.from(this.completedSteps),
      failedSteps: Array.from(this.failedSteps),
      totalSteps: this.initializationSteps.length
    };
  }

  /**
   * Setup initialization steps with proper dependencies
   */
  private setupInitializationSteps(): void {
    this.initializationSteps = [
      {
        name: 'cache-manager',
        initialize: async () => {
          // Cache manager initialization is handled internally
          logger.info('Cache manager ready', { module: 'SystemInitializer' });
        },
        required: false,
        dependencies: []
      },
      {
        name: 'api-monitoring',
        initialize: async () => {
          // API monitoring service is ready by default
          logger.info('API monitoring service ready', { module: 'SystemInitializer' });
        },
        required: false,
        dependencies: []
      },
      {
        name: 'performance-optimization',
        initialize: async () => {
          try {
            await performanceInitializer.initialize();
            logger.info('Performance optimization initialized', { module: 'SystemInitializer' });
          } catch (error) {
            logger.warn('Performance optimization failed to initialize (non-critical)', {
              module: 'SystemInitializer',
              metadata: { error: (error as Error).message }
            });
            // Don't throw - this is non-critical
          }
        },
        required: false,
        dependencies: ['cache-manager', 'api-monitoring']
      }
    ];
  }

  /**
   * Execute initialization steps in dependency order
   */
  private async executeInitializationSequence(config: SystemInitConfig): Promise<void> {
    // Filter steps based on configuration
    const enabledSteps = this.initializationSteps.filter(step => {
      if (step.name === 'performance-optimization' && !config.enablePerformanceOptimization) {
        return false;
      }
      if (step.name === 'cache-manager' && !config.enableCaching) {
        return false;
      }
      if (step.name === 'api-monitoring' && !config.enableMonitoring) {
        return false;
      }
      return true;
    });

    // Execute steps in dependency order
    const executedSteps = new Set<string>();
    
    while (executedSteps.size < enabledSteps.length) {
      let progress = false;
      
      for (const step of enabledSteps) {
        if (executedSteps.has(step.name)) {
          continue;
        }

        // Check if dependencies are satisfied
        const dependenciesSatisfied = step.dependencies.every(dep => 
          this.completedSteps.has(dep) || this.failedSteps.has(dep)
        );

        if (dependenciesSatisfied) {
          try {
            logger.info(`Initializing ${step.name}`, { module: 'SystemInitializer' });
            await step.initialize();
            this.completedSteps.add(step.name);
            executedSteps.add(step.name);
            progress = true;
          } catch (error) {
            this.failedSteps.add(step.name);
            executedSteps.add(step.name);
            
            if (step.required) {
              logger.error(`Required step ${step.name} failed`, error as Error, {
                module: 'SystemInitializer'
              });
              throw error;
            } else {
              logger.warn(`Optional step ${step.name} failed`, {
                module: 'SystemInitializer',
                metadata: { error: (error as Error).message }
              });
              progress = true;
            }
          }
        }
      }

      if (!progress) {
        const remainingSteps = enabledSteps
          .filter(step => !executedSteps.has(step.name))
          .map(step => step.name);
        
        throw new Error(`Circular dependency or missing dependency detected. Remaining steps: ${remainingSteps.join(', ')}`);
      }
    }
  }

  /**
   * Setup development tools
   */
  private setupDevelopmentTools(): void {
    (window as Window & { systemTools?: Record<string, unknown> }).systemTools = {
      getStatus: () => this.getStatus(),
      getPerformanceStatus: () => performanceInitializer.getStatus(),
      getCacheStats: () => cacheManager.getMetrics(),
      getMonitoringStatus: () => apiMonitoringService.getSystemHealth(),
      reinitialize: (config?: Partial<SystemInitConfig>) => {
        this.initialized = false;
        this.completedSteps.clear();
        this.failedSteps.clear();
        return this.initialize(config);
      }
    };

    logger.info('Development tools available at window.systemTools', {
      module: 'SystemInitializer'
    });
  }

  /**
   * Graceful shutdown
   */
  shutdown(): void {
    try {
      if (this.completedSteps.has('performance-optimization')) {
        performanceInitializer.shutdown();
      }
      
      logger.info('System shutdown completed', { module: 'SystemInitializer' });
    } catch (error) {
      logger.error('Error during system shutdown', error as Error, {
        module: 'SystemInitializer'
      });
    }
  }
}

// Create singleton instance
export const systemInitializer = new SystemInitializer();

// Export initialization function
export const initializeSystem = async (config?: Partial<SystemInitConfig>): Promise<void> => {
  return systemInitializer.initialize(config);
};

export default systemInitializer;
